import {
  CONSENTABLE_NOTIFICATION_CHANNELS,
  DONOR_CONSENT_SOURCES,
  DONOR_MATCH_SIGNALS,
  donationStatusSchema,
  donorKindSchema,
  donorLinkRequestStatusSchema,
  donorLinkSignalSchema,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { listRequestSchema } from './common/list.js';
import { moneySchema } from './common/money.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { phoneSchema } from './common/phone.js';
import { queryBooleanSchema } from './common/query.js';
import { generatedFileSchema } from './generated-file.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const throttled = { ...guarded, TOO_MANY_REQUESTS: commonErrors.TOO_MANY_REQUESTS };

const donorMatchSignalSchema = z.enum(DONOR_MATCH_SIGNALS);

const nameSchema = z.string().trim().min(2).max(200);
const emailSchema = z.email().trim().max(320);
const countryIsoSchema = z
  .string()
  .trim()
  .length(2)
  .transform((value) => value.toUpperCase());

export const donorSchema = z.object({
  id: z.uuid(),
  kind: donorKindSchema,
  name: z.string().nullable(),
  taxId: z.string().nullable(),
  email: z.string().nullable(),
  /** The telephone as it was typed — kept exactly, never rewritten. */
  phone: z.string().nullable(),
  /**
   * The same number in E.164, what the donor is matched and messaged on, and
   * what a screen shows and edits; null when the typed value could not be read
   * (docs/decisions/0086).
   */
  phoneE164: z.string().nullable(),
  address: z.string().nullable(),
  countryIso: z.string().nullable(),
  isAnonymous: z.boolean(),
  communicationConsent: z.boolean(),
  consentRecordedAt: z.string().nullable(),
  consentSource: z.string().nullable(),
  institutionId: z.uuid(),
  institutionName: z.string(),
  institutionCode: z.string(),
  countryId: z.uuid(),
  countryName: z.string(),
  zoneId: z.uuid(),
  zoneName: z.string(),
  regionId: z.uuid(),
  regionName: z.string(),
  mergedIntoDonorId: z.uuid().nullable(),
  rootDonorId: z.uuid(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /**
   * Whether the donor belongs to the caller's own scope. A donor reached only
   * through a donation the caller may read, or through an accepted
   * `donor_links` grant, is readable and never writable, so the screen locks
   * its actions instead of offering a write the server will refuse.
   */
  owned: z.boolean(),
});

export type Donor = z.infer<typeof donorSchema>;

/**
 * A candidate the caller could already have found on their own: it is inside
 * their scope, so naming it discloses nothing. The cross-institution lookup is
 * `existsElsewhere` and answers a boolean instead.
 */
export const donorDuplicateSchema = z.object({
  donor: donorSchema,
  score: z.number().int().min(0),
  signals: z.array(donorMatchSignalSchema),
  isDecisive: z.boolean(),
});

export type DonorDuplicate = z.infer<typeof donorDuplicateSchema>;

/**
 * A candidate for the entry autocomplete: enough to tell one person from
 * another on screen and nothing more. The telephone is **masked** the way every
 * other screen shows a donor's, because picking a payer out of a list is not a
 * reason to be handed their number; the full value is on the donor's own page,
 * behind `donor.view`.
 *
 * `owned` is false for a donor an accepted `donor_links` row reaches, exactly
 * as `donorSchema.owned` is — the picker offers it, the donors screen does not.
 * The score, the signals and `isDecisive` are `scoreDonorMatch`'s, never a
 * second scoring rule.
 */
export const donorCandidateSchema = z.object({
  id: z.uuid(),
  name: z.string().nullable(),
  maskedPhone: z.string().nullable(),
  institutionName: z.string(),
  countryName: z.string(),
  owned: z.boolean(),
  score: z.number().int().min(0),
  signals: z.array(donorMatchSignalSchema),
  isDecisive: z.boolean(),
});

export type DonorCandidate = z.infer<typeof donorCandidateSchema>;

/**
 * At least one of a name fragment and a telephone. Both absent is a search over
 * the whole donor base, which is `donor.list`'s job and is paged and sorted for
 * it.
 */
export const donorCandidateQuerySchema = paginationInputSchema
  .extend({
    name: z.string().trim().min(2).max(200).optional(),
    phone: phoneSchema.optional(),
    countryIso: countryIsoSchema.optional(),
  })
  .refine(
    (value) => value.name !== undefined || value.phone !== undefined,
    'a name fragment or a telephone is required',
  );

/**
 * Existence, and nothing else. No name, no institution, no donor id, no count,
 * no masked value — docs/decisions/0024-donors-dedup-and-merge.md. Widening
 * this shape is the decision being reversed, not an implementation detail.
 */
export const donorExistsElsewhereSchema = z.object({
  exists: z.boolean(),
  signal: donorLinkSignalSchema,
});

export const donorLinkRequestSchema = z.object({
  id: z.uuid(),
  donorId: z.uuid(),
  requestingInstitutionId: z.uuid(),
  requestingInstitutionName: z.string(),
  matchedSignal: donorLinkSignalSchema,
  status: donorLinkRequestStatusSchema,
  requestedAt: z.string(),
  respondedAt: z.string().nullable(),
  expiresAt: z.string(),
});

export type DonorLinkRequest = z.infer<typeof donorLinkRequestSchema>;

export const donorLinkSchema = z.object({
  id: z.uuid(),
  donorId: z.uuid(),
  institutionId: z.uuid(),
  institutionName: z.string(),
  grantedAt: z.string(),
});

export const donorMergeSchema = z.object({
  id: z.uuid(),
  survivorDonorId: z.uuid(),
  survivorName: z.string().nullable(),
  mergedDonorId: z.uuid(),
  mergedName: z.string().nullable(),
  mergedByUserId: z.uuid(),
  mergedByUserName: z.string().nullable(),
  mergedAt: z.string(),
  reason: z.string().nullable(),
  reversedAt: z.string().nullable(),
});

export type DonorMerge = z.infer<typeof donorMergeSchema>;

/** One normalized value held by more than one donor, with the rows that hold it. */
export const donorDuplicateGroupSchema = z.object({
  signal: donorMatchSignalSchema,
  value: z.string(),
  donors: z.array(
    z.object({
      id: z.uuid(),
      name: z.string().nullable(),
      institutionId: z.uuid(),
      institutionName: z.string(),
    }),
  ),
});

/**
 * The CRM panel of docs/plans/04-donations.md §4.3, measured across the merge
 * group — every donor sharing this one's `root_donor_id` — and always through
 * the caller's own donation scope, so a figure never counts a donation the
 * caller could not read.
 */
export const donorCrmSchema = z.object({
  /** One row per currency given in; a donor who gave in three currencies has three. */
  totals: z.array(z.object({ total: moneySchema, donationCount: z.number().int() })),
  donationCount: z.number().int().min(0),
  firstGiftAt: z.string().nullable(),
  lastGiftAt: z.string().nullable(),
  /**
   * How often this donor gives, as whole days between gifts. Null under two
   * gifts, because one gift is not a frequency.
   */
  averageDaysBetweenGifts: z.number().int().nullable(),
  /** The campaign categories this donor gives to, most given to first. */
  categories: z.array(
    z.object({
      categoryId: z.uuid(),
      categoryName: z.string(),
      icon: z.string().nullable(),
      donationCount: z.number().int(),
    }),
  ),
  /**
   * The most recent gifts of the merge group. Empty, with `historyVisible`
   * false, for a caller who may read the donor and not donations: the
   * aggregates are the donor's own record, a donation row is not.
   */
  history: z.array(
    z.object({
      id: z.uuid(),
      referenceCode: z.string(),
      donatedAt: z.string(),
      amount: moneySchema,
      status: donationStatusSchema,
      campaignName: z.string(),
      donorId: z.uuid(),
    }),
  ),
  historyVisible: z.boolean(),
  /**
   * The most recent gifts somebody made **on this donor's behalf** — *Bağış
   * Sahibi* resolved to one of the group (docs/decisions/0079) — with the
   * payer's name. Counted in no figure above: the money is the payer's. Empty
   * whenever `historyVisible` is false, for the same reason.
   */
  onBehalfOfHistory: z.array(
    z.object({
      id: z.uuid(),
      referenceCode: z.string(),
      donatedAt: z.string(),
      amount: moneySchema,
      status: donationStatusSchema,
      campaignName: z.string(),
      payerName: z.string().nullable(),
    }),
  ),
  /** How many donors the group holds, so the panel can say the figures span a merge. */
  groupSize: z.number().int().min(1),
});

export type DonorCrm = z.infer<typeof donorCrmSchema>;

/**
 * *Kalan borç* — the legacy's *Kalan TRY/EUR/USD Borcu* per donor: what this
 * donor's donations come to in each currency, through the caller's own
 * donation scope, so a donation the caller cannot open is in no figure.
 * Counted: every donation that takes money and was neither cancelled nor
 * refunded. `pledged` is their amounts; `collected` their confirmed matches;
 * `awaitingApproval` their matches waiting for a second person; `outstanding`
 * what the ones that can still take money still owe — so for each currency
 * `pledged = collected + awaitingApproval + outstanding`. `owingCount` is how
 * many rows the donation list's `owing` filter shows for the donor.
 *
 * `group` is the same figures over the donor's **merge group** — this donor
 * and every donor merged into it — and is set only on a surviving donor that
 * has absorbed another: a merge moves no donation, so the merged donors' debt
 * stays under their own ids, and `currencies` alone would leave it off the
 * survivor's page. Its `owingCount` is what the donation list shows for
 * `mergeGroupDonorId` and `owing` — docs/decisions/0102 §3, amended.
 */
const donorOutstandingCurrenciesSchema = z.array(
  z.object({
    pledged: moneySchema,
    collected: moneySchema,
    awaitingApproval: moneySchema,
    outstanding: moneySchema,
    donationCount: z.number().int().min(0),
    owingCount: z.number().int().min(0),
  }),
);

export const donorOutstandingSchema = z.object({
  currencies: donorOutstandingCurrenciesSchema,
  group: z
    .object({
      /** Every donor of the group, the survivor included. */
      donorCount: z.number().int().min(2),
      currencies: donorOutstandingCurrenciesSchema,
    })
    .nullable(),
});

export type DonorOutstanding = z.infer<typeof donorOutstandingSchema>;

/**
 * What a donor's profile is made of, without a default anywhere. A create fills
 * the absent ones in below; a patch must not, so the two are built from the
 * same fields rather than one from the other.
 *
 * **`communicationConsent`, `consentRecordedAt` and `consentSource` are not
 * here**, though `donorSchema` still reads all three. They are a summary of
 * `donor_channel_consents`, written only by `donor.setNotificationChannel`, and
 * a profile field a screen could send would be a second way to say what the
 * channel rows already say — the drift
 * docs/decisions/0061-a-donors-consent-is-per-channel-not-a-flag.md §3 refuses,
 * and migration `0075` refuses at the database.
 */
const donorProfileFields = {
  kind: donorKindSchema,
  name: nameSchema.nullable(),
  taxId: z.string().trim().max(64).nullable(),
  email: emailSchema.nullable(),
  phone: phoneSchema.nullable(),
  address: z.string().trim().max(500).nullable(),
  countryIso: countryIsoSchema.nullable(),
  isAnonymous: z.boolean(),
};

/** Entry: an absent field is its default, because a create has nothing to keep. */
const donorProfileSchema = z.object({
  ...donorProfileFields,
  kind: donorProfileFields.kind.default('individual'),
  name: donorProfileFields.name.default(null),
  taxId: donorProfileFields.taxId.default(null),
  email: donorProfileFields.email.default(null),
  phone: donorProfileFields.phone.default(null),
  address: donorProfileFields.address.default(null),
  countryIso: donorProfileFields.countryIso.default(null),
  isAnonymous: donorProfileFields.isAnonymous.default(false),
});

/**
 * A patch: an absent field stays absent, so `DonorService.update` keeps what is
 * stored. `donorProfileSchema.partial()` is **not** this — Zod keeps a
 * `ZodDefault` inside the optional it wraps it in and still fills the value in,
 * so a screen sending one changed field rewrote every other one.
 */
const donorProfilePatchSchema = z.object(donorProfileFields).partial();

/**
 * Exactly one complete value. The lookup answers a question about data the
 * caller may not read, so it takes a whole address or a whole phone number and
 * refuses anything else: a prefix or a fragment would turn one boolean into a
 * search over the donor base.
 */
const completeSignalSchema = z
  .object({
    email: emailSchema.optional(),
    phone: phoneSchema.optional(),
    countryIso: countryIsoSchema.optional(),
  })
  .refine(
    (value) => (value.email === undefined) !== (value.phone === undefined),
    'exactly one of email or phone is required',
  );

/**
 * What the organization may lawfully send this donor on one channel, as both
 * surfaces that set it read it — docs/plans/07-notifications-and-reporting.md
 * §7.1, docs/plans/08-hardening-and-launch.md §8.4.
 *
 * `addressPresent` is the server's answer to whether there is anywhere to send:
 * a channel without one is not offerable, and the screen locks the switch rather
 * than offering a choice the database refuses. **`maskedDestination` is masked**
 * — `yu***@g***.com`, `+90***34` — for the same reason the delivery list is:
 * reading whether a donor may be written to is not a reason to be handed their
 * address.
 *
 * The consent record is who decided, what, when and through what. `recordedBy`
 * is filled only where an operator recorded it; a donor's own decision names
 * nobody, because the donor is not a `users` row.
 */
export const donorChannelConsentSchema = z.object({
  granted: z.boolean(),
  decidedAt: z.string(),
  source: z.enum(DONOR_CONSENT_SOURCES),
  recordedByName: z.string().nullable(),
});

export type DonorChannelConsent = z.infer<typeof donorChannelConsentSchema>;

export const donorNotificationChannelSchema = z.object({
  channel: z.enum(CONSENTABLE_NOTIFICATION_CHANNELS),
  enabled: z.boolean(),
  addressPresent: z.boolean(),
  maskedDestination: z.string().nullable(),
  consent: donorChannelConsentSchema.nullable(),
});

export type DonorNotificationChannel = z.infer<typeof donorNotificationChannelSchema>;

export const donorNotificationChannelsSchema = z.object({
  items: z.array(donorNotificationChannelSchema),
});

export type DonorNotificationChannels = z.infer<typeof donorNotificationChannelsSchema>;

export const donorNotificationChannelInputSchema = z.object({
  id: z.uuid(),
  channel: z.enum(CONSENTABLE_NOTIFICATION_CHANNELS),
  enabled: z.boolean(),
});

export type DonorNotificationChannelInput = z.infer<typeof donorNotificationChannelInputSchema>;

export const DONOR_ERASURE_STATUSES = ['proposed', 'carried_out', 'withdrawn'] as const;

export type DonorErasureStatus = (typeof DONOR_ERASURE_STATUSES)[number];

export const donorErasureSchema = z.object({
  id: z.uuid(),
  donorId: z.uuid(),
  organizationId: z.uuid(),
  donorPrivacyRequestId: z.uuid().nullable(),
  status: z.enum(DONOR_ERASURE_STATUSES),
  proposedByUserId: z.uuid(),
  proposedByUserName: z.string().nullable(),
  proposedAt: z.string(),
  carriedOutByUserId: z.uuid().nullable(),
  carriedOutByUserName: z.string().nullable(),
  carriedOutAt: z.string().nullable(),
  withdrawnByUserId: z.uuid().nullable(),
  withdrawnByUserName: z.string().nullable(),
  withdrawnAt: z.string().nullable(),
  /** Counts of what was destroyed, per table. Never a value. */
  cleared: z.record(z.string(), z.number()),
});

export type DonorErasure = z.infer<typeof donorErasureSchema>;

export const donorPrivacyRequestSchema = z.object({
  id: z.uuid(),
  kind: z.enum(['export', 'erasure']),
  status: z.enum(['open', 'completed', 'refused']),
  organizationId: z.uuid(),
  note: z.string().nullable(),
  createdAt: z.string(),
  respondedAt: z.string().nullable(),
  respondedByUserName: z.string().nullable(),
});

export type DonorPrivacyRequest = z.infer<typeof donorPrivacyRequestSchema>;

/**
 * What the donor-rights card on a donor's page reads: the asks the donor has
 * made, the erasure standing over them, and whether the row has already been
 * erased. It carries no personal field of its own — the page above it already
 * shows those, behind the same permission.
 */
export const donorRightsSchema = z.object({
  donorId: z.uuid(),
  erasedAt: z.string().nullable(),
  erasedByUserName: z.string().nullable(),
  privacyRequests: z.array(donorPrivacyRequestSchema),
  erasures: z.array(donorErasureSchema),
});

export type DonorRights = z.infer<typeof donorRightsSchema>;

/**
 * The request a `donor_data_export` row commits. It names the donor and, where
 * the donor asked for it themselves, the request being answered — nothing else,
 * because the job reads the row rather than trusting a payload.
 */
export const donorDataExportInputSchema = z.object({
  donorId: z.uuid(),
  donorPrivacyRequestId: z.uuid().nullable().default(null),
});

export type DonorDataExportInput = z.infer<typeof donorDataExportInputSchema>;

export const donorContract = {
  list: oc
    .route({
      method: 'GET',
      path: '/donors',
      summary: 'Donors owned within the caller’s scope',
    })
    .errors(guarded)
    .input(
      listRequestSchema.extend({
        institutionId: z.uuid().optional(),
        kind: donorKindSchema.optional(),
        status: z.enum(['all', 'active', 'inactive']).default('all'),
        includeMerged: queryBooleanSchema.default(false),
        /**
         * Also offer the donors an accepted `donor_links` row grants this
         * institution. Off by default, because ownership does not move on a
         * link: the donors screen lists what the caller owns. A picker asks
         * for it, so a donor reachable by id is offered in the list a clerk
         * picks from — docs/decisions/0024-donors-dedup-and-merge.md.
         */
        includeLinked: queryBooleanSchema.default(false),
      }),
    )
    .output(paginated(donorSchema)),

  get: oc
    .route({ method: 'GET', path: '/donors/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ donor: donorSchema, merges: z.array(donorMergeSchema) })),

  /**
   * Scored candidates inside the caller's own scope. It never merges and never
   * blocks a save; a name alone is never decisive.
   */
  findDuplicates: oc
    .route({ method: 'GET', path: '/donors/duplicates' })
    .errors(guarded)
    .input(
      z.object({
        name: z.string().trim().max(200).optional(),
        email: emailSchema.optional(),
        phone: phoneSchema.optional(),
        taxId: z.string().trim().max(64).optional(),
        countryIso: countryIsoSchema.optional(),
        excludeId: z.uuid().optional(),
      }),
    )
    .output(z.object({ items: z.array(donorDuplicateSchema) })),

  /**
   * The entry autocomplete's candidates, ranked by `scoreDonorMatch`. It reads
   * through `donorScopePredicate` like every other donor read, behind the same
   * `donor.list` the picker already needs, and it is **bounded**: the search
   * collects at most `MAX_DONOR_CANDIDATES` rows and pages within them, so
   * there is no unbounded list here either.
   *
   * **It carries no throttle**, and that is the difference between it and
   * `existsElsewhere`. That one answers a question about donors the caller may
   * **not** read, so an unmetered call is an oracle over the whole
   * organization's donor base. This one answers only about donors already
   * inside the caller's own scope or granted to them by an accepted link —
   * rows the same permission already pages through and searches by name,
   * e-mail, telephone and tax id on `donor.list`. It discloses nothing new, so
   * a quota would buy no narrowing and would stop a clerk mid-entry
   * — docs/decisions/0024-donors-dedup-and-merge.md *Three properties*.
   */
  candidates: oc
    .route({
      method: 'GET',
      path: '/donors/candidates',
      summary: 'Ranked donor candidates for a name fragment or a telephone',
    })
    .errors(guarded)
    .input(donorCandidateQuerySchema)
    .output(paginated(donorCandidateSchema)),

  existsElsewhere: oc
    .route({ method: 'GET', path: '/donors/exists-elsewhere' })
    .errors(throttled)
    .input(completeSignalSchema)
    .output(donorExistsElsewhereSchema),

  duplicateReport: oc
    .route({ method: 'GET', path: '/donors/duplicate-report' })
    .errors(guarded)
    .input(listRequestSchema.omit({ search: true }))
    .output(paginated(donorDuplicateGroupSchema)),

  /**
   * The CRM panel. Behind `donor.view` because a donor's giving record is the
   * donor's own record — docs/decisions/0038-an-entity-timeline-is-read-behind-its-own-permission.md
   * — while the individual donation rows need `donation.list` and are left out
   * of the answer without it.
   */
  crm: oc
    .route({ method: 'GET', path: '/donors/{id}/crm' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(donorCrmSchema),

  /**
   * The donor's outstanding per currency. Behind `donor.view`, as the CRM
   * figures are: the sums are the donor's own record, read through the
   * caller's donation scope. The rows behind them are the donation list's,
   * behind its own permission.
   */
  outstanding: oc
    .route({ method: 'GET', path: '/donors/{id}/outstanding' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(donorOutstandingSchema),

  create: oc
    .route({ method: 'POST', path: '/donors' })
    .errors(guarded)
    .input(donorProfileSchema.extend({ institutionId: z.uuid() }))
    .output(z.object({ donor: donorSchema })),

  update: oc
    .route({ method: 'PATCH', path: '/donors/{id}' })
    .errors(guarded)
    .input(
      donorProfilePatchSchema.extend({
        id: z.uuid(),
        /** Deactivation replaces deletion: a donor with a donation is never removed. */
        isActive: z.boolean().optional(),
      }),
    )
    .output(z.object({ donor: donorSchema })),

  /**
   * A merge moves no donation. The loser keeps its rows and gains a pointer, so
   * a receipt issued to it still resolves and the audit trail still reads true.
   */
  merge: oc
    .route({ method: 'POST', path: '/donors/merges' })
    .errors(guarded)
    .input(
      z.object({
        survivorDonorId: z.uuid(),
        mergedDonorId: z.uuid(),
        reason: z.string().trim().max(500).nullable().default(null),
      }),
    )
    .output(z.object({ donor: donorSchema })),

  unmerge: oc
    .route({ method: 'POST', path: '/donors/merges/reversal' })
    .errors(guarded)
    .input(z.object({ mergedDonorId: z.uuid() }))
    .output(z.object({ donor: donorSchema })),

  /**
   * Answered the same way whether or not a donor matched, so the call is not a
   * second existence oracle beside `existsElsewhere`, and charged against the
   * same per-user quota.
   */
  requestLink: oc
    .route({ method: 'POST', path: '/donors/link-requests' })
    .errors(throttled)
    .input(completeSignalSchema)
    .output(z.object({ sent: z.literal(true) })),

  /** The owning institution's inbox. It never names the requester's own draft donor. */
  linkRequests: oc
    .route({ method: 'GET', path: '/donors/link-requests' })
    .errors(guarded)
    .input(
      listRequestSchema.omit({ search: true }).extend({
        status: z.enum(['all', 'open', 'accepted', 'declined', 'expired']).default('open'),
      }),
    )
    .output(paginated(donorLinkRequestSchema)),

  respondToLink: oc
    .route({ method: 'POST', path: '/donors/link-requests/{id}/response' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), decision: z.enum(['accepted', 'declined']) }))
    .output(z.object({ request: donorLinkRequestSchema })),

  links: oc
    .route({ method: 'GET', path: '/donors/{id}/links' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ items: z.array(donorLinkSchema) })),
  /**
   * Which channels this donor may be notified on, with the consent beside each.
   * Behind `donor.view`, like every other read of the donor's record: it says
   * what the organization holds about them, not what it may do.
   */
  notificationChannels: oc
    .route({
      method: 'GET',
      path: '/donors/{id}/notification-channels',
      summary: 'The donor’s channels, their consent, and whether an address exists',
    })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(donorNotificationChannelsSchema),

  /**
   * An operator turning a channel on or off for this donor. **Turning it on is
   * recording a consent and turning it off is recording a withdrawal**, in one
   * act on one switch, because withdrawal must be as easy as consent; and a
   * channel with no address is refused rather than stored, since a preference
   * with nowhere to send is a promise the system cannot keep.
   */
  setNotificationChannel: oc
    .route({
      method: 'PUT',
      path: '/donors/{id}/notification-channels/{channel}',
      summary: 'Record this donor’s consent to a channel, or their withdrawal of it',
    })
    .errors(guarded)
    .input(donorNotificationChannelInputSchema)
    .output(donorNotificationChannelsSchema),

  /**
   * KVKK article 11 and GDPR articles 15 and 17 on one donor: what they have
   * asked for, and what has been done about it. Behind `donor.view` like every
   * other read of the record — asking for a copy is not itself personal data
   * the way the record is.
   */
  rights: oc
    .route({
      method: 'GET',
      path: '/donors/{id}/rights',
      summary: 'This donor’s data-protection asks, and the erasures over them',
    })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(donorRightsSchema),

  /**
   * Everything the organization holds about this donor, as a document the donor
   * can read — never a database dump. It commits a `generated_files` row and
   * the worker draws it; the request itself and the read the job makes are both
   * audited, because an export **is** access to personal data.
   */
  exportData: oc
    .route({
      method: 'POST',
      path: '/donors/{id}/data-export',
      summary: 'Ask for this donor’s own data as a document',
    })
    .errors(throttled)
    .input(z.object({ id: z.uuid(), donorPrivacyRequestId: z.uuid().nullable().default(null) }))
    .output(generatedFileSchema),

  /**
   * One person asking for a donor to be erased. It changes nothing: it raises
   * the case a **second** person must carry out, and no single operator can do
   * both halves — docs/decisions/0062.
   */
  proposeErasure: oc
    .route({ method: 'POST', path: '/donors/{id}/erasure' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), donorPrivacyRequestId: z.uuid().nullable().default(null) }))
    .output(z.object({ erasure: donorErasureSchema })),

  withdrawErasure: oc
    .route({ method: 'POST', path: '/donors/erasures/{id}/withdrawal' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ erasure: donorErasureSchema })),

  /**
   * The second person carrying it out. `confirmName` is the donor's name
   * exactly as the row still holds it: a second, independent guard against the
   * wrong row, since the two-person rule alone cannot tell one donor from
   * another. **It is irreversible** — there is no route that undoes it, because
   * a reversible erasure is not an erasure.
   */
  carryOutErasure: oc
    .route({ method: 'POST', path: '/donors/erasures/{id}/execution' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), confirmName: z.string().trim().min(1).max(200) }))
    .output(z.object({ erasure: donorErasureSchema, donor: donorSchema })),
};
